import type { ClassId, Element } from './constants.ts';

/** Where a class draws its power regeneration from. This is the core asymmetry. */
export type LeySource =
  /** Proximity to your own shrine. Zero inside an enemy shrine. */
  | 'own-shrine'
  /** Proximity to a pool biased to your faction. */
  | 'friendly-pool'
  /** Flat everywhere, independent of the arena. */
  | 'self'
  /** Charges in any pool regardless of bias, holds, then falls off a cliff. */
  | 'any-pool-charge';

export interface ClassDef {
  id: ClassId;
  name: string;
  /** The Magestorm Millennium name, kept for flavour text. */
  altName: string;
  blurb: string;
  leySource: LeySource;
  /** Magicians cannot conquer shrines; Arcanists cannot bias pools. */
  canBiasPools: boolean;
  canConquerShrines: boolean;
  /** Innate flight, no loadout slot spent. Mentalist only. */
  canFly: boolean;
  hpTable: readonly number[];
  ppTable: readonly number[];
  /** Schools this class may draw loadout spells from. */
  schools: readonly string[];
  /** Elements this class deals, for resist-meta reference. */
  elements: readonly Element[];
}

const PP_BASE = [
  6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48,
  51, 54, 57, 60, 63, 66, 69, 72, 75, 78, 81, 84, 87, 90, 96,
] as const;

/** Arcanist loses one PP per level from 12 onward, per the original tables. */
const PP_ARCANIST = PP_BASE.map((v, i) => (i >= 11 ? v - 1 : v));

const HP_MAGICIAN = [
  25, 31, 37, 44, 50, 56, 62, 69, 75, 81, 88, 94, 100, 106, 113,
  119, 125, 132, 138, 144, 150, 157, 163, 169, 175, 182, 188, 194, 200, 206,
] as const;

/** Mentalist and Arcanist share this table. */
const HP_MEDIUM = [
  30, 37, 45, 52, 60, 67, 75, 82, 90, 97, 105, 112, 120, 127, 135,
  142, 150, 157, 165, 172, 180, 187, 195, 202, 210, 217, 225, 232, 240, 247,
] as const;

const HP_CLERIC = [
  35, 44, 52, 61, 70, 79, 87, 96, 105, 113, 122, 131, 140, 148, 157,
  166, 175, 183, 192, 201, 210, 218, 227, 236, 245, 253, 262, 271, 280, 288,
] as const;

export const CLASSES: Record<ClassId, ClassDef> = {
  magician: {
    id: 'magician',
    name: 'Magician',
    altName: 'Wizard',
    blurb:
      'Offensive juggernaut. Highest damage, lowest hit points, and utterly dependent on pools — ' +
      'a Magician far from friendly water is a Magician out of ammunition. Cannot conquer shrines.',
    leySource: 'friendly-pool',
    canBiasPools: true,
    canConquerShrines: false,
    canFly: false,
    hpTable: HP_MAGICIAN,
    ppTable: PP_BASE,
    schools: ['Fire Law', 'Ice Law', 'Earth Law', 'Protections'],
    elements: ['fire', 'cold', 'earth'],
  },
  cleric: {
    id: 'cleric',
    name: 'Cleric',
    altName: 'Healer',
    blurb:
      'Defensive anchor and the only class that can heal or raise the dead. Draws power from its own ' +
      'shrine, so it fights best at home and is nearly inert deep in enemy ground.',
    leySource: 'own-shrine',
    canBiasPools: true,
    canConquerShrines: true,
    canFly: false,
    hpTable: HP_CLERIC,
    ppTable: PP_BASE,
    schools: ['Healing', 'Lifegiving', 'Wounding', 'Barrier Law', 'Prayer'],
    elements: ['physical'],
  },
  mentalist: {
    id: 'mentalist',
    name: 'Mentalist',
    altName: 'Psionic',
    blurb:
      'Scout and skirmisher. Flies, leaps walls, and regenerates the same everywhere it goes — the only ' +
      'class whose power does not care about the map at all.',
    leySource: 'self',
    canBiasPools: true,
    canConquerShrines: true,
    canFly: true,
    hpTable: HP_MEDIUM,
    ppTable: PP_BASE,
    schools: ['Brilliance', 'Mind Attack', 'Movements', 'Damage Resist'],
    elements: ['light'],
  },
  arcanist: {
    id: 'arcanist',
    name: 'Arcanist',
    altName: 'Eldritch',
    blurb:
      'Primal caster and trap-layer. Charges from any pool regardless of who owns it, then runs on ' +
      'borrowed time once it leaves. Deals unresistable void and mana damage. Cannot bias pools.',
    leySource: 'any-pool-charge',
    canBiasPools: false,
    canConquerShrines: true,
    canFly: false,
    hpTable: HP_MEDIUM,
    ppTable: PP_ARCANIST,
    schools: ['Void Law', 'Mana Law', 'Arcane Signs', 'Arcane Protection'],
    elements: ['void', 'mana'],
  },
};

export function hpAt(cls: ClassId, level: number): number {
  const t = CLASSES[cls].hpTable;
  return t[Math.max(0, Math.min(t.length - 1, level - 1))];
}

export function ppAt(cls: ClassId, level: number): number {
  const t = CLASSES[cls].ppTable;
  return t[Math.max(0, Math.min(t.length - 1, level - 1))];
}
